import { SearchFacet, SearchFacetItem } from '@/pages/Search/SearchPage/types';

// Drop a trailing " facet" suffix from search-party's display names so e.g.
// "Materials & Techniques facet" / "Creator facet" / "Category facet" produce the
// same compact key shape as flag-style facets like "Shipping" or "Subject". Then
// strip everything that isn't a letter or digit so display names like
// "Materials & Techniques" collapse to a compact, ampersand-free key
// ("MaterialsTechniques") suitable for the analytics property value.
const sanitizeFacetKey = (name: string): string => name.replace(/\s+facet$/i, '').replace(/[^A-Za-z0-9]/g, '');

// Emit every selected node, ancestors AND leaves, mirroring `getSelectedNestedFacets` in
// `useGetAppliedFilterChipValues`. If a parent and a child are both flagged selected, both
// are reported — the analytics payload preserves the full refinement path the user navigated
// through, not just the most specific leaf.
const collectSelectedCategories = (key: string, nodes: SearchFacetItem[] | null | undefined): string[] => {
    if (!nodes) {
        return [];
    }
    const out: string[] = [];
    for (const node of nodes) {
        if (node.selected && node.name) {
            out.push(`${key}:${node.name}`);
        }
        if (node.categories) {
            out.push(...collectSelectedCategories(key, node.categories));
        }
    }
    return out;
};

/**
 * Build the `Refinement Applied` payload for the Search Refined event.
 *
 * Walks the facet tree from `state.search.facets` and emits a `"<facetKey>:<optionName>"`
 * string for every selected option (flat `options[]`) or selected category leaf
 * (recursive `categories[]`). The key is the facet's display name with non-alphanumeric
 * characters stripped, so taxonomy IDs never reach the analytics payload.
 *
 * Range facets (current bid, hammer price, estimate) are intentionally not included —
 * range UI doesn't map cleanly to the `key:value` shape this property is meant to carry.
 */
export const getAppliedRefinements = (facets: SearchFacet[] | null | undefined): string[] => {
    if (!facets?.length) {
        return [];
    }
    const out: string[] = [];
    for (const facet of facets) {
        const key = sanitizeFacetKey(facet.name ?? '');
        if (!key) {
            continue;
        }
        // search-party emits a facet as either flat (`options[]`, e.g. Materials & Techniques)
        // or hierarchical (`categories.facets[]`, e.g. F1 / F2). They are not expected to be
        // populated together, so the two branches below cannot double-emit the same selection.
        if (facet.options) {
            for (const option of facet.options) {
                if (option.selected && option.name) {
                    out.push(`${key}:${option.name}`);
                }
            }
        }
        if (facet.categories.facets) {
            out.push(...collectSelectedCategories(key, facet.categories.facets));
        }
    }
    return out;
};
